import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { useSimilarOfficialFees } from "../hooks/useOfficialFees";
import { OfficialFeeStatusBadge } from "./OfficialFeeStatusBadge";

interface Props {
  officeName: string;
  officeAccountNumber: string;
  amount: string;
  payingCompany: string;
}

function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatDate(dt: string) {
  return new Date(dt).toLocaleDateString("pl-PL");
}

/**
 * Below the form, warns the requester if fees with the same office, account number,
 * amount and paying company were created in the last 3 days — across ALL fees, so a
 * possible duplicate payment is caught even if someone else ordered it. Who ordered
 * the matching fee is intentionally not shown.
 */
export function SimilarFeesNotice({
  officeName,
  officeAccountNumber,
  amount,
  payingCompany,
}: Props) {
  // Debounce a stable string key so the lookup only fires once typing settles.
  const rawKey = JSON.stringify({
    office_name: (officeName ?? "").trim(),
    office_account_number: (officeAccountNumber ?? "").trim(),
    amount: (amount ?? "").trim(),
    paying_company: payingCompany ?? "",
  });
  const debouncedKey = useDebouncedValue(rawKey, 400);
  const params = useMemo(() => JSON.parse(debouncedKey), [debouncedKey]);

  const { data: fees = [] } = useSimilarOfficialFees(params);

  if (fees.length === 0) return null;

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={16} className="text-amber-600 shrink-0" />
        <h3 className="text-sm font-semibold text-amber-800">
          Podobne opłaty (ostatnie 3 dni)
        </h3>
      </div>
      <p className="text-xs text-amber-700 mb-3">
        Znaleziono opłaty o takich samych danych (urząd, numer konta, kwota, spółka
        płacąca) z ostatnich 3 dni. Sprawdź, czy nie dublujesz płatności.
      </p>
      <ul className="space-y-2">
        {fees.map((fee) => (
          <li
            key={fee.id}
            className="rounded border border-amber-200 bg-white/70 px-3 py-2 text-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <OfficialFeeStatusBadge status={fee.status} />
              <span className="text-xs text-[var(--color-text-secondary)]">
                {formatDate(fee.created_at)}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-[var(--color-text-primary)] truncate">
                {fee.project_short_name} — {fee.project_name}
              </span>
              <span className="font-mono font-medium text-[var(--color-text-primary)] shrink-0">
                {formatAmount(fee.amount)} PLN
              </span>
            </div>
            {fee.description && (
              <p className="mt-1 text-xs text-[var(--color-text-secondary)] whitespace-pre-wrap break-words">
                {fee.description}
              </p>
            )}
            {fee.payment_confirmation_files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {fee.payment_confirmation_files.map((f) =>
                  f.url ? (
                    <a
                      key={f.id}
                      href={f.url}
                      download={f.original_filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                    >
                      <Download size={12} />
                      Potwierdzenie PDF
                    </a>
                  ) : null,
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
